import { collection, doc, deleteDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Notice, { NOTICE_STATUSES } from '../types/Notice';

export const useNoticeMutators = () => {
  const { isGod } = useAuth();
  const { isOrgAdmin, userState } = useData();

  const addNotice = async (notice: Notice): Promise<Notice | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found');
      return null;
    }

    if (!notice.orgId) {
      console.error('Notice orgId not found');
      return null;
    }

    if (!isOrgAdmin(notice.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return null;
    }

    try {
      // Use pre-generated itemId if provided, otherwise generate one
      const noticeRef = notice.itemId
        ? doc(firestore, `org/${notice.orgId}/notice`, notice.itemId)
        : doc(collection(firestore, `org/${notice.orgId}/notice`));
      const newNotice: Notice = {
        ...notice,
        creatorId: userState.userProfile?.itemId,
        createTime: Timestamp.now(),
        itemId: noticeRef.id,
      };

      await setDoc(noticeRef, newNotice);
      return newNotice;
    } catch (error) {
      console.error('Error adding notice:', error);
      return null;
    }
  };

  const updateNotice = async (notice: Partial<Notice>): Promise<void> => {
    if (!notice.itemId || !notice.orgId) {
      console.error('Notice itemId or orgId not found:', notice.itemId, notice.orgId);
      return;
    }

    if (!isGod && !isOrgAdmin(notice.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return;
    }

    try {
      // Extract the orgId and itemId from the notice object as changing them is not allowed
      const { orgId, itemId, ...noticeUpdates } = notice;
      const noticeRef = doc(firestore, `org/${orgId}/notice/${itemId}`);
      await setDoc(noticeRef, { ...noticeUpdates }, { merge: true });
    } catch (error) {
      console.error('Error updating notice:', error);
    }
  };

  const deleteNotice = async (notice: Notice): Promise<void> => {
    if (!notice.itemId || !notice.orgId) {
      console.error('Notice itemId or orgId not found:', notice.itemId, notice.orgId);
      return;
    }

    if (!isGod && !isOrgAdmin(notice.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return;
    }

    try {
      const noticeRef = doc(firestore, `org/${notice.orgId}/notice`, notice.itemId);
      
      if (isGod) {
        // God users can hard delete
        await deleteDoc(noticeRef);
      } else {
        // Regular users soft delete
        await updateDoc(noticeRef, { 
          status: NOTICE_STATUSES.DELETED
        });
      }
    } catch (error) {
      console.error('Error deleting notice:', error);
    }
  };

  const restoreNotice = async (notice: Notice): Promise<void> => {
    if (!notice.itemId || !notice.orgId) {
      console.error('Notice itemId or orgId not found:', notice.itemId, notice.orgId);
      return;
    }

    if (!isGod && !isOrgAdmin(notice.orgId)) {
      console.error('Permission denied: must be admin of organization');
      return;
    }

    try {
      const noticeRef = doc(firestore, `org/${notice.orgId}/notice`, notice.itemId);
      await updateDoc(noticeRef, { 
        status: NOTICE_STATUSES.ACTIVE
      });
    } catch (error) {
      console.error('Error restoring notice:', error);
    }
  };

  return {
    addNotice,
    updateNotice,
    deleteNotice,
    restoreNotice,
  };
};